import React, { useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { CardBody, CardHeader, CardTitle } from '@progress/kendo-react-layout';
import styles from './style.module.scss';
import Card from '@components-clean/core/card';
import { CheckboxController } from '@components-clean/core/core-checkbox';
import { DatePickerController } from '@components-clean/core/date-picker';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import { DropDownListController } from '@components-clean/core/drop-down-list';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { useConditionallySynchronizedState } from '@hooks-clean/index';
import { useTranslation } from '@hooks/index';
import { LOVField, PanelListItem, SubjectProfileDetailsConfigurationFormValues } from './types';

interface Props {
    control: UseFormReturn<SubjectProfileDetailsConfigurationFormValues>['control'];
    defaultValues: SubjectProfileDetailsConfigurationFormValues;
    disabled: boolean;
    dateFieldsLOV: LOVField[];
    sortLOV: LOVField[];
    timelinePanelsListItems: PanelListItem[];
}

const TimelineCard = ({ control, defaultValues, disabled, dateFieldsLOV, sortLOV, timelinePanelsListItems }: Props) => {
    const { t } = useTranslation('admin', {
        keyPrefix: 'subjectConfig.subjectProfileDetailsConfig.timeline',
    });

    const selectedTimelinePanels = useWatch({ control, name: 'timeline.panels' });

    // Panels for right column
    const availableTimelinePanels = useMemo(() => {
        // Panels selected when form loaded
        const defaultValuesIdsSet = new Set(defaultValues.timeline.panels.map((x) => x.id));
        // Panels currently selected
        const selectedIdsSet = new Set(selectedTimelinePanels.map((x) => x.id));

        return (
            timelinePanelsListItems
                // Where panel is active, or panel is inactive but was one of the selected panels when form loaded
                .filter((x) => x.active || defaultValuesIdsSet.has(x.id))
                // Which aren't already selected
                .filter((x) => !selectedIdsSet.has(x.id))
        );
    }, [timelinePanelsListItems, defaultValues.timeline.panels, selectedTimelinePanels]);

    // Working copy of lists for left/right columns of DragDropListCore components
    // Since the DragDropListCore component keeps track of additional properties like `selected` and `filtered`,
    //   which aren't tracked by the `selectedTimelinePanels` form value or `availableTimelinePanels` list

    const [selectedTimelinePanelsForDragDropList, setSelectedTimelinePanelsForDragDropList] =
        useConditionallySynchronizedState<DragDropListItemProps[]>({
            value: selectedTimelinePanels,
            // When not in edit mode, keep list synchronized with form value
            synchronize: disabled,
        });

    const [availableTimelinePanelsForDragDropList, setAvailableTimelinePanelsForDragDropList] =
        useConditionallySynchronizedState<DragDropListItemProps[]>({
            value: availableTimelinePanels,
            // When not in edit mode, keep list synchronized with true list (which doesn't track selected state)
            synchronize: disabled,
        });

    return (
        <Card padding="none" data-testid="subject-profile-details-configuration-timeline" data-type="timeline">
            <CardHeader>
                <CardTitle>{t('title')}</CardTitle>
            </CardHeader>

            <CardBody>
                <div className={styles.fieldList}>
                    <DatePickerController
                        control={control}
                        name="timeline.startDate"
                        required
                        readonly={disabled}
                        data-testid="datepicker-timeline-startDate"
                        label={`${t('field.startDate')}*`}
                        fillMode="solid"
                    />

                    <MultiSelectController
                        control={control}
                        name="timeline.endDateFields"
                        required
                        isEditing
                        disabled={disabled}
                        data-testid="timeline-endDateFields"
                        label={`${t('field.endDateFields')}*`}
                        data={dateFieldsLOV}
                        dataItemKey="key"
                        textField="name"
                        allowCustom={false}
                    />

                    <DropDownListController
                        control={control}
                        name="timeline.sort"
                        required
                        readOnly={disabled}
                        skipDisabledItems={false}
                        data-testid="dropdownlist-timeline-sort"
                        label={`${t('field.sort')}*`}
                        data={sortLOV}
                        dataItemKey="key"
                        textField="name"
                    />

                    <MultiSelectController
                        control={control}
                        name="timeline.milestoneDateFields"
                        isEditing
                        disabled={disabled}
                        data-testid="timeline-milestoneDateFields"
                        label={`${t('field.milestoneDateFields')}`}
                        data={dateFieldsLOV}
                        dataItemKey="key"
                        textField="name"
                        allowCustom={false}
                    />

                    <CheckboxController
                        control={control}
                        name="timeline.showTagsAsMilestones"
                        disabled={disabled}
                        data-testid="timeline-showTagsAsMilestones"
                        label={t('field.showTagsAsMilestones')}
                    />

                    <Controller
                        control={control}
                        name="timeline.panels"
                        render={({ field: { onChange } }) => (
                            <DragDropListsCore
                                list1Header={t('field.panels.selected')}
                                list2Header={t('field.panels.unselected')}
                                idField="id"
                                list1Items={selectedTimelinePanelsForDragDropList}
                                list2Items={availableTimelinePanelsForDragDropList}
                                disabled={disabled}
                                isTwoLists
                                onChangeItemsLeft={(items) => {
                                    // Update working copy, which includes stuff like `selected` and `filtered`
                                    setSelectedTimelinePanelsForDragDropList(items);

                                    // Update true form value, which doesn't include stuff like `selected` and `filtered`
                                    // Because those properties would interfere with the form `isDirty` detection
                                    const mapped = items.map(({ selected, filtered, ...rest }) => rest);
                                    onChange(mapped);
                                }}
                                onChangeItemsRight={setAvailableTimelinePanelsForDragDropList}
                            />
                        )}
                    />
                </div>
            </CardBody>
        </Card>
    );
};

export default TimelineCard;
